import { useCallback, useEffect, useState } from 'react'
import { APIBASE } from '../constants/api.constants'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type { BuildProductDto, BuildVariantDto } from '../models/build.model'
import { isNetworkError, SERVER_UNAVAILABLE_MESSAGE } from '../utils/api/server-unavailable'

/**
 * useBuildProduct — parallel to useShopProduct for /build/:id.
 */
export interface UseBuildProduct {
  readonly product: BuildProductDto | null
  readonly selectedVariant: BuildVariantDto | null
  readonly selectedVariantId: string | null
  readonly isLoading: boolean
  readonly error: string | null
  readonly selectVariant: (variantId: string) => void
  readonly reload: () => void
}

export function useBuildProduct(productId: string | null = null): UseBuildProduct {
  const [product, setProduct] = useState<BuildProductDto | null>(null)
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      const url =
        productId === null
          ? `${APIBASE}${APIROUTES.BUILDPRODUCT}`
          : `${APIBASE}${fillPath(APIROUTES.BUILDPRODUCTITEM, { id: productId })}`
      let res: Response
      try {
        res = await fetch(url)
      } catch (err) {
        if (isNetworkError(err)) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
        throw err
      }
      if (res.status >= 500 || !res.ok) {
        throw new Error(SERVER_UNAVAILABLE_MESSAGE)
      }
      const parsed = (await res.json()) as {
        success: boolean
        data?: { product: BuildProductDto | null }
      }
      const p = parsed.data?.product ?? null
      setProduct(p)
      const preferred = p?.variants.find((v) => v.in_stock) ?? p?.variants[0] ?? null
      setSelectedVariantId(preferred?.variant_id ?? null)
    } catch (e) {
      setError(e instanceof Error ? e.message : SERVER_UNAVAILABLE_MESSAGE)
      setProduct(null)
      setSelectedVariantId(null)
    } finally {
      setIsLoading(false)
    }
  }, [productId])

  useEffect(() => {
    void load()
  }, [load])

  const selectVariant = useCallback((variantId: string): void => {
    setSelectedVariantId(variantId)
  }, [])

  const selectedVariant =
    product?.variants.find((v) => v.variant_id === selectedVariantId) ?? null

  return {
    product,
    selectedVariant,
    selectedVariantId,
    isLoading,
    error,
    selectVariant,
    reload: () => {
      void load()
    }
  }
}
